import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './report-selector';
import type { ReportListItem } from '../types/report';

@customElement('bia-chat-header')
export class BiaChatHeader extends LitElement {
  static styles = css`
    :host {
      display: block;
      background: var(--bia-header-bg, #fff);
      color: var(--bia-header-text, #0f0f0e);
      border-bottom: 1px solid var(--bia-header-border, var(--bia-border, #e8e5dc));
    }
    .top {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      min-height: 48px;
    }
    .brand {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: baseline;
      gap: 10px;
    }
    .wordmark {
      font-family: var(--bia-font-display, Georgia, serif);
      font-style: italic;
      font-weight: 600;
      font-size: 20px;
      line-height: 1;
      letter-spacing: -0.02em;
      color: var(--bia-header-text, var(--bia-text, #0f0f0e));
      flex-shrink: 0;
    }
    .divider {
      width: 1px;
      height: 14px;
      background: currentColor;
      opacity: 0.25;
      flex-shrink: 0;
      align-self: center;
    }
    .subtitle {
      font-family: var(--bia-font-family, sans-serif);
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--bia-header-text-muted, var(--bia-text-muted, #6b6b66));
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
    .actions {
      display: flex;
      align-items: center;
      gap: 2px;
      flex-shrink: 0;
    }
    button.icon {
      background: transparent;
      color: var(--bia-header-text-muted, var(--bia-text-muted, #6b6b66));
      border: none;
      cursor: pointer;
      width: 28px;
      height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      padding: 0;
      transition: background 140ms ease, color 140ms ease;
    }
    button.icon:hover {
      background: rgba(127, 127, 127, 0.12);
      color: var(--bia-header-text, var(--bia-text, #0f0f0e));
    }
    button.icon svg {
      width: 16px;
      height: 16px;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.6;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    button.icon[disabled] {
      cursor: default;
      opacity: 0.55;
    }
    button.icon.refreshing svg {
      animation: bia-spin 720ms linear infinite;
    }
    button.icon.ok {
      color: #1f8a4c;
    }
    @keyframes bia-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    .selector {
      padding: 0 20px 14px;
    }
  `;

  @property({ type: Array }) reports: ReportListItem[] = [];
  @property({ type: Number }) selectedId: number | null = null;
  @property({ type: Boolean }) maximized = false;
  @property({ type: String }) reportName = '';
  @property({ type: String }) displayMode: 'floating' | 'docked' = 'floating';
  @property({ type: Boolean }) dockToggle = false;

  @state() private refreshState: 'idle' | 'loading' | 'ok' = 'idle';
  private _okResetTimer: ReturnType<typeof setTimeout> | null = null;

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._okResetTimer) {
      clearTimeout(this._okResetTimer);
      this._okResetTimer = null;
    }
  }

  private onClose(): void {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }

  private onToggleDock(): void {
    this.dispatchEvent(new CustomEvent('toggledock', { bubbles: true, composed: true }));
  }

  private onToggleMax(): void {
    this.dispatchEvent(new CustomEvent('togglemax', { bubbles: true, composed: true }));
  }

  private onRefreshSchema(): void {
    if (this.refreshState === 'loading') return;
    this.refreshState = 'loading';
    // The parent listens, performs the HTTP call, and then calls
    // `markRefreshDone()` via a DOM CustomEvent round-trip. If it never
    // comes back, we still auto-release after 8s so the button doesn't
    // stay stuck spinning.
    this.dispatchEvent(
      new CustomEvent('refreshschema', { bubbles: true, composed: true }),
    );
    setTimeout(() => {
      if (this.refreshState === 'loading') {
        this.refreshState = 'idle';
      }
    }, 8000);
  }

  /** Parent calls this after a successful refresh to show the brief
   * "done" green state before returning to idle. */
  markRefreshDone(): void {
    this.refreshState = 'ok';
    if (this._okResetTimer) clearTimeout(this._okResetTimer);
    this._okResetTimer = setTimeout(() => {
      this.refreshState = 'idle';
      this._okResetTimer = null;
    }, 1500);
  }

  /** Parent calls this on failure to release the spinner. */
  markRefreshFailed(): void {
    this.refreshState = 'idle';
  }

  private cleanReportName(name: string): string {
    // The report name is shown verbatim as the host platform provides it.
    // Previous versions stripped a "Grupo do Master - " prefix to save
    // horizontal space, but that silently hid meaningful naming and made
    // the header inconsistent with the picker list.
    return name;
  }

  render() {
    const showLegacySelector = this.reports.length > 0;
    const subtitle = this.reportName ? this.cleanReportName(this.reportName) : '';
    return html`
      <div class="top">
        <div class="brand">
          <span class="wordmark">BIA</span>
          ${subtitle
            ? html`<span class="divider"></span><span class="subtitle">${subtitle}</span>`
            : null}
        </div>
        <div class="actions">
          <button
            class="icon ${this.refreshState === 'loading' ? 'refreshing' : ''} ${this.refreshState === 'ok' ? 'ok' : ''}"
            @click=${this.onRefreshSchema}
            ?disabled=${this.refreshState === 'loading'}
            aria-label="Recarregar dados e permissões do relatório"
            title=${
              this.refreshState === 'loading'
                ? 'Recarregando...'
                : this.refreshState === 'ok'
                ? 'Atualizado'
                : // The button re-mints the session BEFORE refreshing the
                  // schema, so it also reloads filters, permissions and the
                  // LLM key. The old copy named only the schema, which left
                  // the recovery path for a stale filter invisible: a user
                  // whose filter had been fixed on the platform had no
                  // reason to press the one button that would pick it up.
                  'Recarregar o relatório: medidas, tabelas, colunas e também seus filtros e permissões'
            }
          >
            ${this.refreshState === 'ok'
              ? html`<svg viewBox="0 0 24 24"><path d="M5 12l5 5 9-11"/></svg>`
              : html`<svg viewBox="0 0 24 24"><path d="M20 11A8 8 0 1 0 18.93 16M20 4v5h-5"/></svg>`}
          </button>
          ${this.dockToggle
            ? html`<button
                class="icon"
                @click=${this.onToggleDock}
                aria-label=${this.displayMode === 'docked' ? 'Flutuar' : 'Encaixar na lateral'}
                title=${this.displayMode === 'docked' ? 'Flutuar' : 'Encaixar na lateral'}
              >
                ${this.displayMode === 'docked'
                  ? html`<svg viewBox="0 0 24 24"><rect x="3" y="5" width="13" height="10" rx="2"/><rect x="12" y="11" width="9" height="8" rx="2"/></svg>`
                  : html`<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="14" y1="4" x2="14" y2="20"/></svg>`}
              </button>`
            : ''}
          <button
            class="icon"
            @click=${this.onToggleMax}
            aria-label=${this.maximized ? 'Minimizar' : 'Maximizar'}
            title=${this.maximized ? 'Minimizar' : 'Maximizar'}
          >
            ${this.maximized
              ? html`<svg viewBox="0 0 24 24"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"/></svg>`
              : html`<svg viewBox="0 0 24 24"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>`}
          </button>
          <button class="icon" @click=${this.onClose} aria-label="Fechar" title="Fechar">
            <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </div>
      </div>
      ${showLegacySelector
        ? html`
            <div class="selector">
              <bia-report-selector
                .reports=${this.reports}
                .selectedId=${this.selectedId}
              ></bia-report-selector>
            </div>
          `
        : null}
    `;
  }
}
